import Pagination from "@dana/MobiusOverrides/Pagination/Pagination";
import { themeBackgrounds, themeColors } from "@dana/MobiusOverrides/styles/colors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import DataTable, { DataTablePresentationProps } from "@insite/mobius/DataTable";
import { DataTableCellProps } from "@insite/mobius/DataTable/DataTableCell";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Img, { ImgProps } from "@insite/mobius/Img";
import { DataTableBody, DataTableCell, DataTableRow } from "@insite/mobius/index";
import LoadingOverlay from "@insite/mobius/LoadingOverlay";
import Select, { SelectProps } from "@insite/mobius/Select";
import TextField from "@insite/mobius/TextField";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { ReactNode, useEffect, useState } from "react";
import { css } from "styled-components";

interface KingPinResultScreenProps {
    make: string;
    model: string;
    fields?: any;
}

interface FindKingPinResultStyles {
    wrapper?: InjectableCss;
    contentWrapper?: InjectableCss;
    rowWrapper?: InjectableCss;
    formWrapper?: InjectableCss;
    twoColWrapper?: InjectableCss;
    twoColResultWrapper?: InjectableCss;
    heading?: TypographyPresentationProps;
    diagramImage?: ImgProps;
    productImage?: ImgProps;
    productInfoWrapper?: InjectableCss;
    productPartNumbersWrapper?: InjectableCss;
    productPartNumbersStyles?: TypographyPresentationProps;
    productPartNumbersNoLinkStyles?: TypographyPresentationProps;
    productDescriptionStyles?: TypographyPresentationProps;
    totalResultText?: TypographyPresentationProps;
    attributeTable?: DataTablePresentationProps;
    attributeTableCell?: DataTableCellProps;
    searchButton?: ButtonPresentationProps;
}

export const findKingPinResultStyles: FindKingPinResultStyles = {
    wrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 16px;
            padding: 16px 16px 16px 16px;
        `,
    },
    contentWrapper: {
        css: css`
            background: ${themeBackgrounds.white};
            padding: 15px 40px 100px 50px;
            height: auto;
            display: flex;
            flex-direction: column;
            align-items: left;
        `,
    },
    formWrapper: {
        css: css`
            width: 100%;
            max-width: 340px;
            display: flex;
            flex-direction: column;
            gap: 16px;
        `,
    },
    twoColWrapper: {
        css: css`
            display: flex;
            flex-direction: row;
            gap: 16px;
            width: 100%;
            & > * {
                flex: 1 1 0;
                min-width: 0;
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            flex-direction: column;
                            align-items: stretch;
                            gap: 12px;
                            & > * {
                                flex: unset;
                            }
                        `,
                        null,
                        null,
                        null,
                        null,
                    ],
                    "max",
                )}
        `,
    },

    twoColResultWrapper: {
        css: css`
            display: flex;
            flex-direction: row;
            gap: 16px;
            width: 100%;
            & > *:first-child {
                flex: 0 0 30%;
                max-width: 30%;
            }
            & > *:last-child {
                flex: 0 0 70%;
                max-width: 70%;
            }

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            flex-direction: column;
                            & > * {
                                flex: unset;
                                max-width: 100%;
                                width: 100%;
                            }
                            & > *:first-child {
                                flex: unset;
                                max-width: 100%;
                                width: 100%;
                            }
                            & > *:last-child {
                                flex: unset;
                                max-width: 100%;
                                width: 100%;
                            }
                        `,
                        null,
                        null,
                        null,
                        null,
                    ],
                    "minMax",
                )}
        `,
    },
    heading: {
        variant: "h3",
    },
    diagramImage: {
        css: css`
            width: 306px;
            height: 560px;
            max-width: 100%;
            max-height: 100%;
            object-fit: contain;
        `,
    },
    productImage: {
        css: css`
            width: 50px;
            min-width: 50px;
            max-width: 50px;
            height: auto;
            object-fit: contain;

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            width: 100%;
                            max-width: 100%;
                            display: block;
                        `,
                        null,
                        null,
                        null,
                        null,
                    ],
                    "minMax",
                )}
        `,
    },
    totalResultText: {
        variant: "h4",
        css: css`
            margin-bottom: 0;
        `,
    },
    attributeTable: {
        css: css`
            width: 100%;
            border-collapse: collapse;
            border: 1px solid ${themeBackgrounds.lightGray};
        `,
    },
    attributeTableCell: {
        css: css`
            word-break: break-word;
            white-space: normal;
        `,
    },
    searchButton: {
        variant: "primary",
    },
    productPartNumbersWrapper: {
        css: css`
            a {
                text-decoration: none;
            }
        `,
    },
    productPartNumbersStyles: {
        css: css`
            color: ${themeColors.primary.danaBlue};
            text-decoration: none;
            transition: text-decoration 0.2s;
            &:hover {
                text-decoration: underline;
            }
        `,
        variant: "h4",
    },
    productPartNumbersNoLinkStyles: {
        css: css`
            color: ${themeBackgrounds.darkGray};
        `,
        variant: "h4",
    },
    productDescriptionStyles: {
        css: css`
            color: ${themeColors.primary.richBlack};
        `,
    },
    rowWrapper: {
        css: css`
            display: flex;
            flex-direction: row;
            flex-wrap: wrap;
            gap: 16px;
            width: 100%;
            box-sizing: border-box;
        `,
    },

    productInfoWrapper: {
        css: css`
            background: ${themeBackgrounds.white};
            padding: 20px 20px 20px 20px;
            flex: 0 0 calc(50% - 8px);
            max-width: calc(50% - 8px);
            box-sizing: border-box;
            display: flex;
            flex-direction: row;
            gap: 16px;
            cursor: pointer;

            & > * {
                flex: 1 1 0;
                min-width: 0;
            }

            &:focus,
            &:focus-visible {
                outline-color: ${themeColors.secondary.danaDarkBlue};
                outline-style: solid;
                outline-width: 1px;
                outline-offset: 2px;
            }

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            flex: 0 0 100%;
                            max-width: 100%;
                            padding: 10px 10px 10px 10px;
                            flex-direction: column;
                            align-items: stretch;
                            gap: 15px;
                            & > * {
                                width: 100%;
                                min-width: 0;
                            }
                        `,
                        css`
                            flex: 0 0 100%;
                            max-width: 100%;
                        `,
                        null,
                        null,
                        null,
                    ],
                    "minMax",
                )}
        `,
    },
};

type KingPinSet = {
    erpNumber: string;
    partName: string;
    partImage: string;
    partUrl: string;
    brand: string;
    model: string;
};

const KingPinResultScreen: React.FC<KingPinResultScreenProps> = ({ make, model, fields }) => {
    const styles = findKingPinResultStyles;
    const [showLoadingOverlay, setShowLoadingOverlay] = useState(false);
    const [selectedMake, setSelectedMake] = useState(make);
    const [selectedModel, setSelectedModel] = useState(model);
    const [makesData, setMakesData] = useState<string[]>([]);
    const [modelsData, setModelsData] = useState<string[]>([]);
    const totalResultMessage = translate("Showing {0} results for King Pin Kits");
    const [searchResultMessage, setSearchResultMessage] = useState("");
    const [result, setResult] = useState<KingPinSet | undefined | null>(null);
    const [totalResult, setTotalResult] = useState(0);

    const fetchMakes = async () => {
        setShowLoadingOverlay(true);
        try {
            const result = await fetch(`/api/v1/findKingPin/makes`);
            if (!result.ok) {
                throw new Error(result.statusText);
            }
            const data = await result.json();
            setMakesData(data);
        } catch (error) {
            console.error("Error fetching makes:", error);
        } finally {
            setShowLoadingOverlay(false);
        }
    };

    const fetchModels = async (make: string) => {
        setShowLoadingOverlay(true);
        try {
            const result = await fetch(`/api/v1/findKingPin/models?make=${make}`);
            if (!result.ok) {
                throw new Error(result.statusText);
            }
            const data = await result.json();
            setModelsData(data);
        } catch (error) {
            console.error("Error fetching models:", error);
        } finally {
            setShowLoadingOverlay(false);
        }
    };

    const search = () => {
        if (!selectedMake || !selectedModel) return;

        const params = new URLSearchParams({
            axleManufacturer: selectedMake,
            axelModel: selectedModel,
        });
        window.location.search = `?${params.toString()}`;
    };

    const handleManufacturerChange: SelectProps["onChange"] = event => {
        const selectedValue = event.target.value;
        setSelectedModel("");
        setSelectedMake(selectedValue);
        fetchModels(selectedValue);
    };

    const handleModelChange: SelectProps["onChange"] = event => {
        setSelectedModel(event.target.value);
    };

    const fetchResult = async () => {
        setShowLoadingOverlay(true);
        try {
            const result = await fetch(`/api/v1/findKingPin/search`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({
                    make: selectedMake,
                    model: selectedModel,
                }),
            });
            if (!result.ok) {
                throw new Error(result.statusText);
            }
            const data = await result.json();
            if (data && data.erpNumber) {
                setTotalResult(1);
                setSearchResultMessage(totalResultMessage.replace("{0}", "1"));
            }
            setResult(data);
        } catch (error) {
            console.error("Error fetching result:", error);
        } finally {
            setShowLoadingOverlay(false);
        }
    };

    const handleOpenPartUrl = (partUrl?: string) => {
        if (!partUrl) return;
        window.open(partUrl, "_blank", "noreferrer");
    };

    useEffect(() => {
        fetchMakes();
        fetchModels(selectedMake);
        fetchResult();
    }, []);

    return (
        <LoadingOverlay
            loading={showLoadingOverlay}
            css={css`
                width: 100%;
            `}
        >
            <StyledWrapper {...styles.wrapper} className="wrapper">
                <StyledWrapper {...styles.twoColWrapper}>
                    <StyledWrapper {...styles.contentWrapper}>
                        <Typography {...styles.heading} as="h3">
                            {translate("Find King Pin Kits")}
                        </Typography>
                        <StyledWrapper {...styles.formWrapper} className="form-wrapper">
                            <Select
                                label={translate("Truck/Axle Manufacturer")}
                                name="manufacturer"
                                onChange={handleManufacturerChange}
                                value={selectedMake}
                            >
                                <option value="">{translate("Select Truck/Axle Manufacturer")}</option>
                                {makesData &&
                                    makesData.map(s => (
                                        <option key={s} value={s}>
                                            {s}
                                        </option>
                                    ))}
                            </Select>
                            <Select
                                label={translate("Truck/Axle Model Number")}
                                name="model"
                                onChange={handleModelChange}
                                disabled={!selectedMake}
                                value={selectedModel}
                            >
                                <option value="">{translate("Select Truck/Axle Model Number")}</option>
                                {modelsData &&
                                    modelsData.map(s => (
                                        <option key={s} value={s}>
                                            {s}
                                        </option>
                                    ))}
                            </Select>
                            <Button
                                {...styles.searchButton}
                                onClick={search}
                                disabled={!selectedMake || !selectedModel}
                            >
                                {translate("GO")}
                            </Button>
                        </StyledWrapper>
                    </StyledWrapper>
                    <StyledWrapper {...styles.contentWrapper}>
                        <Typography {...styles.heading} as="h3">
                            {translate("Assembly Diagram")}
                        </Typography>
                        <Img
                            {...styles.diagramImage}
                            src={fields?.assemblyDiagramImage}
                            altText={fields?.assemblyDiagramImageAltText || ""}
                        />
                    </StyledWrapper>
                </StyledWrapper>

                <Typography {...styles.totalResultText} as="h4">
                    {searchResultMessage}
                </Typography>

                {result && (
                    <StyledWrapper {...styles.rowWrapper}>
                        <StyledWrapper
                            {...styles.productInfoWrapper}
                            onClick={() => handleOpenPartUrl(result?.partUrl)}
                            tabIndex={0}
                            role="button"
                            onKeyDown={e => {
                                if (e.key === "Enter" || e.key === " ") {
                                    e.preventDefault();
                                    handleOpenPartUrl(result?.partUrl);
                                }
                            }}
                        >
                            <StyledWrapper {...styles.twoColResultWrapper}>
                                <StyledWrapper>
                                    <Img {...styles.productImage} src={result.partImage} altText={result.partName} />
                                </StyledWrapper>
                                <StyledWrapper {...styles.productPartNumbersWrapper}>
                                    {result.partUrl ? (
                                        <Typography {...styles.productPartNumbersStyles}>{result.erpNumber}</Typography>
                                    ) : (
                                        <Typography {...styles.productPartNumbersNoLinkStyles}>
                                            {result.erpNumber}
                                        </Typography>
                                    )}
                                    <Typography {...styles.productDescriptionStyles} as="span">
                                        {result.partName}
                                    </Typography>
                                </StyledWrapper>
                            </StyledWrapper>
                            <DataTable {...styles.attributeTable}>
                                <DataTableBody>
                                    <DataTableRow>
                                        <DataTableCell {...styles.attributeTableCell}>
                                            <Typography as="b">{translate("Brand")}</Typography>
                                        </DataTableCell>
                                        <DataTableCell {...styles.attributeTableCell}>
                                            {!result.brand || result.brand.toLowerCase() === "null"
                                                ? "Spicer"
                                                : result.brand}
                                        </DataTableCell>
                                    </DataTableRow>
                                    <DataTableRow>
                                        <DataTableCell {...styles.attributeTableCell}>
                                            <Typography as="b">{translate("Model")}</Typography>
                                        </DataTableCell>
                                        <DataTableCell {...styles.attributeTableCell}>
                                            {!result.model || result.model.toLowerCase() === "null" ? "" : result.model}
                                        </DataTableCell>
                                    </DataTableRow>
                                </DataTableBody>
                            </DataTable>
                        </StyledWrapper>
                    </StyledWrapper>
                )}
            </StyledWrapper>
        </LoadingOverlay>
    );
};

export default KingPinResultScreen;
